import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaHome,
  FaInfoCircle,
  FaUserGraduate,
  FaBuilding,
  FaEnvelope,
  FaTimes,
} from "react-icons/fa";
import { headerData, getRoute } from "../../data/headerData";

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { contactInfo, schoolInfo, navigation, buttons, menu, footer } =
    headerData;

  // Simple toggle functions
  const openMenu = () => {
    setIsMenuOpen(true);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Map of icons for navigation items
  const navIcons = {
    Home: FaHome,
    About: FaInfoCircle,
    Admissions: FaUserGraduate,
    Facilities: FaBuilding,
    Contact: FaEnvelope,
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const sidebarVariants = {
    hidden: { x: "100%" },
    visible: {
      x: 0,
      transition: { type: "tween", duration: 0.3, ease: "easeInOut" },
    },
    exit: {
      x: "100%",
      transition: { type: "tween", duration: 0.3, ease: "easeInOut" },
    },
  };

  // Helper function to render contact item based on type
  const renderContactItem = (item, key) => {
    const baseClasses =
      "flex items-center gap-2 text-[12px] hover:text-yellow-500 duration-100 font-[500]";
    const IconComponent = item.icon;

    switch (item.type) {
      case "phone":
        return (
          <div className={baseClasses} key={key}>
            <IconComponent />
            <a href={`tel:${item.number}`} className="block">
              {item.displayNumber}
            </a>
          </div>
        );

      case "email":
        return (
          <div className={baseClasses} key={key}>
            <IconComponent />
            <a href={`mailto:${item.address}`} className="block">
              {item.displayEmail}
            </a>
          </div>
        );

      case "address":
        return (
          <div className={baseClasses} key={key}>
            <IconComponent />
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              {item.text}
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div id="root">
      <div
        data-rht-toaster=""
        style={{
          position: "fixed",
          zIndex: 9999,
          inset: 16,
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <header className="relative">
        {/* Top Bar */}
        <div className="px-4 py-2 text-white bg-indigo-800">
          <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-5">
              {/* Phone */}
              {renderContactItem(contactInfo.phone, "phone")}

              {/* Email */}
              {renderContactItem(contactInfo.email, "email")}
            </div>

            {/* Address */}
            {renderContactItem(contactInfo.address, "address")}
          </div>
        </div>

        {/* Main Header */}
        <div className="sticky top-0 z-50 transition-all duration-300 bg-white shadow-lg">
          <div className="container px-4 mx-auto">
            <div className="flex items-center justify-between py-3 md:py-4">
              {/* Logo and School Name */}
              <div className="flex items-center space-x-3 md:space-x-4">
                <Link
                  className="flex items-center space-x-3 md:space-x-4 group"
                  to={getRoute("HOME_ROUTE")}
                >
                  <div className="relative">
                    <div className="absolute -inset-1.5 bg-blue-100 rounded-full blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
                    <img
                      className="relative w-auto h-10 transition duration-300 transform md:h-12 lg:h-14 group-hover:scale-105"
                      alt="logo"
                      src={schoolInfo.logo}
                    />
                  </div>
                  <div className="block">
                    <h1 className="text-[12px] font-bold leading-tight tracking-tight text-gray-900 transition-colors duration-300 md:text-xl lg:text-2xl group-hover:text-blue-800">
                      {schoolInfo.name}
                    </h1>
                    <p className="text-[10px] sm:text-[10px] text-gray-600 mt-0.5 font-medium group-hover:text-gray-800 transition-colors duration-300">
                      {schoolInfo.tagline}
                    </p>
                  </div>
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="items-center hidden space-x-6 lg:flex">
                <nav className="flex items-center space-x-1">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      className="relative px-4 py-2 font-semibold tracking-wide text-gray-700 transition-all duration-300 hover:text-blue-700 group"
                      to={getRoute(item.routeKey)}
                    >
                      <span className="relative z-10 text-[15px] transition-all duration-300 group-hover:translate-y-[-2px] group-hover:scale-[1.05]">
                        {item.name}
                      </span>
                      <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-600 transform scale-x-0 group-hover:scale-x-100 transition-all duration-300 origin-center"></div>
                      <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-blue-400 blur-sm opacity-0 group-hover:opacity-100 transform scale-x-0 group-hover:scale-x-100 transition-all duration-300 origin-center"></div>
                    </Link>
                  ))}
                </nav>

                {/* Apply Now Button */}
                <div className="flex items-center ml-4 space-x-4">
                  <Link to={getRoute(buttons.applyNow.routeKey)}>
                    <button className="relative group">
                      <div className="absolute inset-0 transition duration-500 opacity-0 bg-gradient-to-r from-blue-600 to-green-500 rounded-xl blur-lg group-hover:opacity-40"></div>
                      <div className="relative py-3 text-[12px] font-semibold text-white transition-all duration-300 bg-gradient-to-r from-blue-700 to-blue-600 px-7 rounded-xl group-hover:-translate-y-1 group-hover:shadow-xl">
                        {buttons.applyNow.text}
                      </div>
                    </button>
                  </Link>
                </div>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="relative lg:hidden group"
                onClick={openMenu}
                aria-label="Open menu"
              >
                <div className="absolute -inset-1.5 bg-blue-50 rounded-full blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
                <div className="relative flex items-center justify-center transition-colors duration-300 border border-gray-200 rounded-lg w-9 h-9 group-hover:border-blue-300">
                  <menu.icon className="w-5 h-5 text-gray-700 group-hover:text-blue-700" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar with Animation */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop with fade animation */}
            <motion.div
              className="fixed inset-0 z-40 bg-black/50 lg:hidden"
              onClick={closeMenu}
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.3 }}
            />

            {/* Sidebar with slide animation */}
            <motion.div
              className="fixed top-0 right-0 z-50 h-full max-w-full overflow-y-auto bg-white shadow-2xl w-72 lg:hidden"
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center space-x-3">
                    <img
                      className="w-auto h-10"
                      alt="logo"
                      src={schoolInfo.logo}
                    />
                    <div>
                      <h2 className="text-base font-bold text-gray-900">
                        {schoolInfo.name}
                      </h2>
                      <p className="text-[10px] font-semibold text-blue-700">
                        {schoolInfo.subtitle}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeMenu}
                    className="flex items-center justify-center w-8 h-8 transition-colors rounded-lg hover:bg-gray-100"
                    aria-label="Close menu"
                  >
                    <FaTimes className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
                <p className="pt-3 text-[10px] italic text-gray-600 border-t border-gray-200">
                  {schoolInfo.tagline}
                </p>
              </div>

              <nav className="p-5">
                <h3 className="mb-3 text-[10px] font-semibold tracking-wider text-gray-500 uppercase">
                  Navigation
                </h3>
                <div className="space-y-1">
                  {navigation.map((item, index) => {
                    const IconComponent = navIcons[item.name];
                    return (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Link
                          className="group flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-blue-50 transition-all duration-200 border border-transparent hover:border-blue-100"
                          to={getRoute(item.routeKey)}
                          onClick={closeMenu}
                        >
                          <div className="text-[12px] text-blue-600 transition-colors group-hover:text-blue-700">
                            <IconComponent />
                          </div>
                          <span className="text-[12px] font-medium text-gray-700 transition-colors group-hover:text-blue-700">
                            {item.name}
                          </span>
                          <div className="ml-auto transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                          </div>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>

                <div className="pt-5 mt-6 border-t border-gray-100">
                  <Link
                    to={getRoute(buttons.applyNow.routeKey)}
                    onClick={closeMenu}
                  >
                    <motion.button
                      className="relative w-full mb-3 group"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-green-500 rounded-lg blur opacity-0 group-hover:opacity-60 transition duration-300"></div>
                      <div className="relative bg-gradient-to-r from-blue-700 to-blue-600 text-white py-2.5 rounded-lg text-[12px] font-semibold hover:from-blue-800 hover:to-blue-700 transition-all duration-300">
                        {buttons.applyNow.text}
                      </div>
                    </motion.button>
                  </Link>
                </div>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="text-gray-300 bg-gray-900 border-t border-gray-800">
        <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-4">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
            {/* About Section */}
            <div className="space-y-6">
              <div className="flex items-center">
                <Link
                  className="flex items-center space-x-3 md:space-x-4 group"
                  to={getRoute("HOME_ROUTE")}
                >
                  <img
                    className="relative w-auto h-10 transition duration-300 transform md:h-12 lg:h-24 group-hover:scale-105"
                    alt="logo"
                    src={schoolInfo.logo}
                  />
                  <div className="block">
                    <h1 className="text-[12px] font-bold text-transparent transition-all duration-300 md:text-xl lg:text-lg bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text group-hover:from-blue-300 group-hover:to-purple-300">
                      {schoolInfo.name}
                    </h1>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-blue-300 font-semibold text-[10px] md:text-[12px] tracking-wide group-hover:text-blue-200 transition-colors duration-300">
                        {schoolInfo.subtitle}
                      </p>
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse group-hover:bg-green-300 transition-colors duration-300" />
                    </div>
                  </div>
                </Link>
              </div>
              <p className="pt-2 text-[12px] leading-relaxed text-gray-400">
                {footer.about.description}
              </p>
              <p className="text-[10px] text-gray-500">
                {footer.about.accreditation}
              </p>
              <p className="text-[10px] italic text-gray-500">
                {footer.about.motto}
              </p>

              {/* Social Links */}
              <div className="flex pt-2 space-x-3">
                {footer.socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className="relative group"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                  >
                    <div className="absolute transition-opacity duration-300 rounded-full opacity-0 -inset-1 bg-gradient-to-r from-blue-600 to-purple-600 blur group-hover:opacity-40" />
                    <div className="relative flex items-center justify-center w-10 h-10 text-gray-400 transition-all duration-300 bg-gray-800 border border-gray-700 rounded-full group-hover:border-blue-500 group-hover:text-white">
                      <social.icon />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Section */}
            <div>
              <h3 className="flex items-center mb-4 text-[12px] font-semibold tracking-wider text-gray-200 uppercase">
                <span className="mr-2 text-blue-500">
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    role="img"
                    viewBox="0 0 24 24"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M1.6367 1.6367C.7322 1.6367 0 2.369 0 3.2734v17.4532c0 .9045.7322 1.6367 1.6367 1.6367h20.7266c.9045 0 1.6367-.7322 1.6367-1.6367V3.2734c0-.9045-.7322-1.6367-1.6367-1.6367H1.6367zm.545 2.1817h19.6367v16.3632h-2.7266v-1.0898h-4.9102v1.0898h-12V3.8184zM12 8.1816c-.9046 0-1.6367.7322-1.6367 1.6368 0 .9045.7321 1.6367 1.6367 1.6367.9046 0 1.6367-.7322 1.6367-1.6367 0-.9046-.7321-1.6368-1.6367-1.6368zm-4.3633 1.9102c-.6773 0-1.2285.5493-1.2285 1.2266 0 .6772.5512 1.2265 1.2285 1.2265.6773 0 1.2266-.5493 1.2266-1.2265 0-.6773-.5493-1.2266-1.2266-1.2266zm8.7266 0c-.6773 0-1.2266.5493-1.2266 1.2266 0 .6772.5493 1.2265 1.2266 1.2265.6773 0 1.2285-.5493 1.2285-1.2265 0-.6773-.5512-1.2266-1.2285-1.2266zM12 12.5449c-1.179 0-2.4128.4012-3.1484 1.0059-.384-.1198-.8043-.1875-1.2149-.1875-1.3136 0-2.7285.695-2.7285 1.5586v.8965h14.1836v-.8965c0-.8637-1.4149-1.5586-2.7285-1.5586-.4106 0-.831.0677-1.2149.1875-.7356-.6047-1.9694-1.0059-3.1484-1.0059Z" />
                  </svg>
                </span>
                Quick Links
              </h3>
              <ul className="space-y-3">
                {footer.quickLinks.map((link, index) => (
                  <li key={index} className="text-[12px]">
                    {link.external ? (
                      <a
                        href={link.external}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-gray-400 transition-all duration-300 hover:text-blue-400 group"
                      >
                        <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-300" />
                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                          {link.name}
                        </span>
                        <span className="ml-2 text-[10px] transition-opacity opacity-0 group-hover:opacity-100">
                          ↗
                        </span>
                      </a>
                    ) : (
                      <Link
                        to={getRoute(link.routeKey)}
                        className="flex items-center text-gray-400 transition-all duration-300 hover:text-blue-400 group"
                      >
                        <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-300" />
                        <span className="transition-transform duration-300 group-hover:translate-x-1">
                          {link.name}
                        </span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Academics Section */}
            <div>
              <h3 className="flex items-center mb-4 text-[12px] font-semibold tracking-wider text-gray-200 uppercase">
                <span className="mr-2 text-blue-500">
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 640 512"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M622.34 153.2L343.4 67.5c-15.2-4.67-31.6-4.67-46.79 0L17.66 153.2c-23.54 7.23-23.54 38.36 0 45.59l48.63 14.94c-10.67 13.19-17.23 29.28-17.88 46.9C38.78 266.15 32 276.11 32 288c0 10.78 5.68 19.85 13.86 25.65L20.33 428.53C18.11 438.52 25.71 448 35.94 448h56.11c10.24 0 17.84-9.48 15.62-19.47L82.14 313.65C90.32 307.85 96 298.78 96 288c0-11.57-6.47-21.25-15.66-26.87.76-15.02 8.44-28.3 20.69-36.72L296.6 284.5c9.06 2.78 26.44 6.25 46.79 0l278.95-85.7c23.55-7.24 23.55-38.36 0-45.6zM352.79 315.09c-28.53 8.76-52.84 3.92-65.59 0l-145.02-44.55L128 384c0 35.35 85.96 64 192 64s192-28.65 192-64l-14.18-113.47-145.03 44.56z" />
                  </svg>
                </span>
                Academics
              </h3>
              <ul className="space-y-3">
                {footer.academics.map((item, index) => (
                  <li key={index} className="text-[12px]">
                    <Link
                      to={getRoute(item.routeKey)}
                      className="flex items-center text-gray-400 transition-all duration-300 hover:text-blue-400 group"
                    >
                      <span className="w-1.5 h-1.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full mr-3 opacity-0 group-hover:opacity-100 transform group-hover:scale-110 transition-all duration-300" />
                      <span className="transition-transform duration-300 group-hover:translate-x-1">
                        {item.name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Section */}
            <div className="space-y-6">
              <h3 className="mb-2 text-lg font-bold text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">
                Contact Us
              </h3>
              <div className="space-y-4 text-[10px]">
                <a
                  href={contactInfo.address.link}
                  className="block"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="flex items-start space-x-3 cursor-pointer group">
                    <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
                      <contactInfo.address.icon />
                    </div>
                    <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
                      {contactInfo.address.text}
                    </span>
                  </div>
                </a>
                <a href={`tel:${contactInfo.phone.number}`} className="block">
                  <div className="flex items-start space-x-3 cursor-pointer group">
                    <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
                      <contactInfo.phone.icon />
                    </div>
                    <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
                      {contactInfo.phone.displayNumber}
                    </span>
                  </div>
                </a>
                <a
                  href={`mailto:${contactInfo.email.address}`}
                  className="block"
                >
                  <div className="flex items-start space-x-3 cursor-pointer group">
                    <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
                      <contactInfo.email.icon />
                    </div>
                    <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
                      {contactInfo.email.displayEmail}
                    </span>
                  </div>
                </a>
                <div>
                  <div className="flex items-start space-x-3 cursor-pointer group">
                    <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
                      <footer.workingHours.icon />
                    </div>
                    <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
                      {footer.workingHours.days}
                    </span>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${footer.whatsapp.number}`}
                  className="block"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="flex items-start space-x-3 cursor-pointer group">
                    <div className="flex-shrink-0 mt-1 text-blue-400 transition-colors duration-200 group-hover:text-blue-300">
                      <footer.whatsapp.icon />
                    </div>
                    <span className="text-gray-300 transition-colors duration-200 group-hover:text-white">
                      WhatsApp: {footer.whatsapp.display}
                    </span>
                  </div>
                </a>
              </div>
              <div className="pt-4 border-t border-gray-700">
                <p className="text-[10px] text-gray-500">
                  Need immediate assistance?
                  <a
                    href={`tel:${contactInfo.phone.number}`}
                    className="ml-2 text-blue-400 transition-colors hover:text-blue-300"
                  >
                    Call us now
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright Bar */}
        <div className="bg-gray-800 border-t border-gray-700">
          <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <div className="flex items-center mb-4 text-[12px] text-gray-500 md:mb-0">
                {footer.copyright.text}
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-[12px]">
                {footer.quickLinks.map((link, index) => (
                  <div key={index}>
                    {link.external ? (
                      <a
                        href={link.external}
                        className="text-gray-500 transition-colors duration-200 hover:text-blue-400"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={getRoute(link.routeKey)}
                        className="text-gray-500 transition-colors duration-200 hover:text-blue-400"
                      >
                        {link.name}
                      </Link>
                    )}
                    {index < footer.quickLinks.length - 1 && (
                      <span className="ml-4 text-gray-600">•</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
